import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CostRow = ({ p, value, onChange, onEnter }) => {
  const cost = parseFloat(value) || 0;
  const sale = Number(p.precio_1) || 0;
  const g = sale - (Number(p.default_commission) || 0) - cost;
  return (
    <tr data-testid={`cost-row-${p.id}`}>
      <td className="px-3 py-1.5">
        <div className="font-medium">{p.name}{p.variant_name && <span className="text-primary"> · {p.variant_name}</span>}</div>
        <div className="text-[11px] text-slate-500">SKU: {p.sku || '—'}</div>
      </td>
      <td className="px-3 py-1.5 text-right">{m(sale)}</td>
      <td className="px-3 py-1.5 text-right">{m(p.default_commission)}</td>
      <td className="px-3 py-1.5 w-36">
        <Input type="number" min="0" step="0.01" value={value} onChange={e => onChange(e.target.value)} onKeyDown={onEnter}
          placeholder="Costo" className="h-8 text-right cost-input" data-testid={`cost-input-${p.id}`} />
      </td>
      <td className={`px-3 py-1.5 text-right font-semibold ${!cost ? 'text-slate-400' : g < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
        {cost ? `${m(g)}${sale ? ` · ${((g / sale) * 100).toFixed(1)}%` : ''}` : '—'}
      </td>
    </tr>
  );
};

export const BulkCostDialog = ({ open, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [vals, setVals] = useState({});
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/products`, { withCredentials: true }).then(({ data }) => {
      const list = (data || []).filter(p => !p.has_variants && p.active !== false)
        .sort((a, b) => `${a.name} ${a.variant_name || ''}`.localeCompare(`${b.name} ${b.variant_name || ''}`));
      setItems(list);
      setVals(Object.fromEntries(list.map(p => [p.id, p.purchase_cost > 0 ? String(p.purchase_cost) : ''])));
    }).catch(() => toast.error('Error al cargar productos'));
  }, [open]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter(p => (!onlyMissing || !(p.purchase_cost > 0))
      && (!s || `${p.name} ${p.variant_name || ''} ${p.sku || ''}`.toLowerCase().includes(s)));
  }, [items, onlyMissing, q]);

  const changed = items.filter(p => (vals[p.id] || '') !== (p.purchase_cost > 0 ? String(p.purchase_cost) : ''));

  const focusNext = (e) => {
    if (e.key !== 'Enter') return;
    const all = [...document.querySelectorAll('.cost-input')];
    all[all.indexOf(e.target) + 1]?.focus();
  };

  const save = async () => {
    if (!changed.length) return toast.info('No hay cambios');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/products/bulk-cost`,
        { items: changed.map(p => ({ id: p.id, cost: parseFloat(vals[p.id]) || 0 })) }, { withCredentials: true });
      toast.success(`${data.updated} costos guardados`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="bulk-cost-dialog">
        <DialogHeader><DialogTitle>💲 Captura rápida de costos</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar nombre o SKU…" className="w-64 h-9" data-testid="bulk-cost-search" />
          <label className="flex items-center gap-1"><input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} data-testid="bulk-cost-only-missing" /> Solo sin costo</label>
          <span className="text-slate-500">{shown.length} productos · Enter pasa al siguiente</span>
        </div>
        <div className="overflow-y-auto flex-1 border rounded">
          <table className="w-full text-sm" data-testid="bulk-cost-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0"><tr>
              <th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-right">Precio 1</th><th className="px-3 py-2 text-right">Comisión</th>
              <th className="px-3 py-2 text-left">Costo</th><th className="px-3 py-2 text-right">Ganancia P1</th></tr></thead>
            <tbody className="divide-y">{shown.map(p => (
              <CostRow key={p.id} p={p} value={vals[p.id] || ''} onEnter={focusNext} onChange={(v) => setVals(s => ({ ...s, [p.id]: v }))} />
            ))}</tbody>
          </table>
        </div>
        <DialogFooter className="items-center">
          <span className="text-sm text-slate-600 mr-auto" data-testid="bulk-cost-changed">{changed.length} cambios sin guardar</span>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || !changed.length} data-testid="bulk-cost-save">{saving ? 'Guardando…' : 'Guardar costos'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
